import { useEffect, useState } from 'react';
import { clsx } from '../../../ds/clsx';
import { usePrefersReducedMotion } from '../../../ds/hooks';
import type { Glow } from '../api';
import { Eyebrow } from '../ui';
import styles from './session.module.css';

/** How long the card stands at its peak (Terminierung 09 note: about 1.2 seconds), then the shrink. */
export const GLOW_PEAK_MS = 1200;
const SHRINK_MS = 380;

/**
 * Terminierung 09 — Glow-up after a booking: the surface washes out (sand at 86 % with a 3 px blur), the
 * appointment card stands large in the middle with three figures, then shrinks towards the timeline where
 * the appointment stays as an event. Reduced motion: the card stands still for the same time and goes.
 */
export function GlowOverlay({ glow, onDone }: { glow: Glow; onDone: () => void }) {
  const reducedMotion = usePrefersReducedMotion();
  const [phase, setPhase] = useState<'peak' | 'shrink'>('peak');

  useEffect(() => {
    const peak = window.setTimeout(() => {
      if (reducedMotion) onDone();
      else setPhase('shrink');
    }, GLOW_PEAK_MS);
    return () => window.clearTimeout(peak);
  }, [reducedMotion, onDone]);

  useEffect(() => {
    if (phase !== 'shrink') return;
    const done = window.setTimeout(onDone, SHRINK_MS);
    return () => window.clearTimeout(done);
  }, [phase, onDone]);

  const figures = glow.metrics.cells.filter((cell) => cell.key !== 'talk' && cell.key !== 'reached');
  return (
    <div
      className={clsx(
        styles.glow,
        reducedMotion ? styles.glowStatic : styles.glowAnimated,
        phase === 'shrink' && styles.glowOut,
      )}
      role="status"
      aria-live="assertive"
    >
      <div className={clsx(styles.glowCard, phase === 'shrink' && styles.glowCardShrink)}>
        <Eyebrow tone="accent">Termin vereinbart</Eyebrow>
        <div className={styles.glowWhen}>
          {glow.dateLabel}
          <br />
          {glow.timeLabel}
        </div>
        <div className={styles.glowLine}>{glow.line}</div>
        <div className={styles.glowCalendar}>{glow.calendarLine}</div>
        <div className={styles.glowFigures}>
          {figures.map((cell) => (
            <div key={cell.key}>
              <Eyebrow>
                {cell.key === 'per_talk_hour' ? 'Pro Stunde Talktime' : cell.label.replace(' · Rekord', '')}
              </Eyebrow>
              <div
                className={clsx(styles.glowFigure, cell.key === 'appointments' && styles.glowFigureAccent)}
              >
                {cell.value}
                {cell.key === 'streak' && cell.sub ? ` ${cell.sub}` : ''}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
